import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUser } from "./userContext";
import { createApiClient } from "./apiClient";
import useAuthToken from "../hooks/useAuthToken";
import { protectedResources } from "../authConfig";
import type {
  ResolvedFeature,
  SubscriptionStatus,
  TierContextValue,
  TierLevel,
} from "../types/UserTypes";

// FreeCrew defaults used on error (fail-closed)
const FALLBACK_TIER: TierLevel = "free_crew";
const FALLBACK_STATUS: SubscriptionStatus = "active";

// Dev/E2E tier override (development or explicit staging E2E builds only)
const DEV_TIER_OVERRIDE_KEY = "dev_tier_override";
const E2E_OVERRIDES_ENABLED =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_ENABLE_E2E_OVERRIDES === "true";
const TIER_RANK: Record<TierLevel, number> = {
  free_crew: 0,
  core: 1,
  pro: 2,
  enterprise: 3,
};

export function getDevTierOverride(): TierLevel | null {
  if (!E2E_OVERRIDES_ENABLED) return null;
  const val = localStorage.getItem(DEV_TIER_OVERRIDE_KEY);
  if (val && val in TIER_RANK) return val as TierLevel;
  return null;
}

export function setDevTierOverride(tier: TierLevel | null): void {
  if (tier) {
    localStorage.setItem(DEV_TIER_OVERRIDE_KEY, tier);
  } else {
    localStorage.removeItem(DEV_TIER_OVERRIDE_KEY);
  }
}

function applyTierOverride(
  features: ResolvedFeature[],
  overrideTier: TierLevel
): ResolvedFeature[] {
  const overrideRank = TIER_RANK[overrideTier];
  return features.map((f) => {
    // No requiredTier — trust the API-resolved values (avoids undoing backend override)
    if (!f.requiredTier) return f;

    const requiredRank = TIER_RANK[f.requiredTier] ?? 999;
    const tierIncluded = overrideRank >= requiredRank;
    const enabled = tierIncluded && !f.adminDisabled;
    // When override enables a feature the real tier doesn't have, promote level
    const level = enabled && !f.enabled
      ? (overrideRank > requiredRank ? "advanced" : "standard")
      : f.level;
    return { ...f, tierIncluded, enabled, level };
  });
}

const defaultValue: TierContextValue = {
  tier: FALLBACK_TIER,
  subscriptionStatus: FALLBACK_STATUS,
  currentPeriodEnd: null,
  canceledAt: null,
  gracePeriodEnd: null,
  features: [],
  visibility: [],
  loading: true,
  error: false,
  hasFeature: () => false,
  isVisible: () => false,
  getFeatureLevel: () => null,
  getLimit: () => null,
  getFeature: () => undefined,
  refreshTier: async () => {},
};

const TierContext = createContext<TierContextValue>(defaultValue);

export const useTier = () => useContext(TierContext);

const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export const TierProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, activeTenantId } = useUser();
  const { token, getToken } = useAuthToken();

  const [tier, setTier] = useState<TierLevel>(FALLBACK_TIER);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus>(FALLBACK_STATUS);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const [canceledAt, setCanceledAt] = useState<string | null>(null);
  const [gracePeriodEnd, setGracePeriodEnd] = useState<string | null>(null);
  const [features, setFeatures] = useState<ResolvedFeature[]>([]);
  const [visibility, setVisibility] = useState<import("../types/UserTypes").VisibilitySetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const lastFetchRef = useRef<number>(0);

  const initialLoadDone = useRef(false);

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const fetchTier = useCallback(async (silent = false) => {
    if (!api) return;

    if (!silent) {
      setLoading(true);
      setError(false);
    }

    try {
      const [sub, featBody, visBody] = await Promise.all([
        api.get<Record<string, unknown>>("subscription"),
        api.get<ResolvedFeature[] | { features: ResolvedFeature[] }>("subscription/features"),
        api.get<any[]>("visibility").catch(() => []),
      ]);

      const feat: ResolvedFeature[] = Array.isArray(featBody)
        ? featBody
        : Array.isArray((featBody as any)?.features)
          ? (featBody as any).features
          : [];

      setTier((sub as any).tier ?? FALLBACK_TIER);
      setSubscriptionStatus((sub as any).status ?? (sub as any).subscriptionStatus ?? FALLBACK_STATUS);
      setCurrentPeriodEnd((sub as any).currentPeriodEnd ?? null);
      setCanceledAt((sub as any).canceledAt ?? null);
      setGracePeriodEnd((sub as any).gracePeriodEnd ?? null);
      setFeatures(feat);
      setVisibility(Array.isArray(visBody) ? visBody : []);
      setError(false);
      lastFetchRef.current = Date.now();
      initialLoadDone.current = true;
    } catch (err) {
      console.error("Failed to load tier data:", err);
      if (!initialLoadDone.current) {
        // First load failed — fall back to free tier and show error banner
        setTier(FALLBACK_TIER);
        setSubscriptionStatus(FALLBACK_STATUS);
        setCurrentPeriodEnd(null);
        setCanceledAt(null);
        setGracePeriodEnd(null);
        setFeatures([]);
        setVisibility([]);
        setError(true);
      }
      // Subsequent refresh failed — keep existing cached data, don't show error
    } finally {
      setLoading(false);
    }
  }, [api]);

  const refreshTier = useCallback(async () => {
    await fetchTier(true);
  }, [fetchTier]);

  // Fetch on mount and when tenant/token changes
  useEffect(() => {
    if (initialLoadDone.current) {
      fetchTier(true); // silent refresh — keep existing UI stable
    } else {
      fetchTier();
    }
  }, [fetchTier]);

  // Refresh on tab visibility (stale after 5 min)
  useEffect(() => {
    const onVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastFetchRef.current > STALE_THRESHOLD_MS
      ) {
        fetchTier(true);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [fetchTier]);

  // Helper methods (memoized on features array, with dev override applied)
  const devOverride = getDevTierOverride();
  const effectiveTier = devOverride ?? tier;
  const effectiveFeatures = devOverride ? applyTierOverride(features, devOverride) : features;

  const helpers = useMemo(() => {
    const featureMap = new Map(effectiveFeatures.map((f) => [f.key, f]));
    const visMap = new Map(visibility.map((v) => [v.key, v.visible]));

    return {
      hasFeature: (key: string): boolean => {
        const f = featureMap.get(key);
        return f?.enabled ?? false;
      },
      isVisible: (key: string): boolean => {
        // Before visibility data loads, hide by default (fail-closed)
        if (visMap.size === 0) return false;
        return visMap.get(key) ?? true;
      },
      getFeatureLevel: (key: string): string | null => {
        const f = featureMap.get(key);
        return f?.enabled ? (f.level ?? null) : null;
      },
      getLimit: (key: string): number | null => {
        const f = featureMap.get(key);
        return f?.limit ?? null;
      },
      getFeature: (key: string): ResolvedFeature | undefined => {
        return featureMap.get(key);
      },
    };
  }, [effectiveFeatures, visibility]);

  const value: TierContextValue = useMemo(
    () => ({
      tier: effectiveTier,
      subscriptionStatus,
      currentPeriodEnd,
      canceledAt,
      gracePeriodEnd,
      features: effectiveFeatures,
      visibility,
      loading,
      error,
      ...helpers,
      refreshTier,
    }),
    [
      effectiveTier,
      subscriptionStatus,
      currentPeriodEnd,
      canceledAt,
      gracePeriodEnd,
      effectiveFeatures,
      visibility,
      loading,
      error,
      helpers,
      refreshTier,
    ]
  );

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
};
